import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Eye,
  EyeOff,
  User,
  Lock
} from "lucide-react";
import { useAuth } from "../auth/AuthContext";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setError("");
    setSubmitting(true);

    try {
      await login(username, password);
      const from = location.state?.from?.pathname || "/";
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.message || "Login failed.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background:
          "linear-gradient(135deg,#0F3D99 0%, #1E63F3 45%, #EAF2FF 100%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute",
          width: 700,
          height: 700,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(255,255,255,.18), transparent 70%)",
          filter: "blur(20px)",
        }}
      />

      <div
        style={{
          width: 440,
          background: "rgba(255,255,255,.95)",
          backdropFilter: "blur(20px)",
          borderRadius: 24,
          padding: 42,
          boxShadow: "0 25px 60px rgba(0,0,0,.18)",
          border: "1px solid rgba(255,255,255,.35)",
          position: "relative",
          zIndex: 2,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 14,
            marginBottom: 35,
          }}
        >
          <img
            src="/oticLogo.png"
            alt="OTIC"
            style={{
              width: 48,
              height: 48,
              objectFit: "contain",
            }}
          />

          <div>
            <div
              style={{
                fontWeight: 800,
                fontSize: 22,
                color: "#123B93",
              }}
            >
              Payment Portal
            </div>

            <div
              style={{
                color: "#64748B",
                fontSize: 14,
              }}
            >
              OTIC Staff Access
            </div>
          </div>
        </div>

        <h2
          style={{
            margin: 0,
            fontSize: 28,
            fontWeight: 700,
            color: "#1E293B",
          }}
        >
          Welcome Back
        </h2>

        <p
          style={{
            marginTop: 8,
            marginBottom: 28,
            color: "#64748B",
            lineHeight: 1.5,
          }}
        >
          Sign in with your staff credentials to continue.
        </p>

        <form onSubmit={submit}>
          <div style={{ marginBottom: 18 }}>
            <label
              style={{
                display: "block",
                marginBottom: 8,
                fontWeight: 600,
                fontSize: 14,
              }}
            >
              Username
            </label>

            <div style={{ position: "relative" }}>
              <User
                size={18}
                style={{
                  position: "absolute",
                  left: 14,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#94A3B8",
                }}
              />

              <input
                value={username}
                autoFocus
                placeholder="Enter username"
                onChange={(e) => setUsername(e.target.value)}
                style={{
                  width: "100%",
                  height: 52,
                  paddingLeft: 44,
                  borderRadius: 12,
                  border: "1px solid #D6DCE7",
                  outline: "none",
                  fontSize: 15,
                  boxSizing: "border-box",
                }}
              />
            </div>
          </div>

          <div style={{ marginBottom: 18 }}>
            <label
              style={{
                display: "block",
                marginBottom: 8,
                fontWeight: 600,
                fontSize: 14,
              }}
            >
              Password
            </label>

            <div style={{ position: "relative" }}>
              <Lock
                size={18}
                style={{
                  position: "absolute",
                  left: 14,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#94A3B8",
                }}
              />

              <input
                type={showPassword ? "text" : "password"}
                value={password}
                placeholder="Enter password"
                onChange={(e) => setPassword(e.target.value)}
                style={{
                  width: "100%",
                  height: 52,
                  paddingLeft: 44,
                  paddingRight: 48,
                  borderRadius: 12,
                  border: "1px solid #D6DCE7",
                  outline: "none",
                  fontSize: 15,
                  boxSizing: "border-box",
                }}
              />

              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                style={{
                  position: "absolute",
                  right: 14,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "#64748B",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {error && (
            <div
              style={{
                background: "#FEF2F2",
                color: "#B91C1C",
                border: "1px solid #FECACA",
                borderRadius: 12,
                padding: 14,
                marginBottom: 20,
                fontSize: 14,
              }}
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            style={{
              width: "100%",
              height: 54,
              border: "none",
              borderRadius: 12,
              cursor: "pointer",
              background:
                "linear-gradient(135deg,#2563EB,#1D4ED8)",
              color: "#fff",
              fontWeight: 700,
              fontSize: 16,
              transition: ".25s",
            }}
          >
            {submitting ? "Signing in..." : "Sign In"}
          </button>
        </form>

        <div
          style={{
            marginTop: 30,
            textAlign: "center",
            color: "#94A3B8",
            fontSize: 13,
          }}
        >
          OTIC Payment Plan Management System
        </div>
      </div>
    </div>
  );
}